import { Button, SxProps, Theme, useTheme, keyframes } from '@mui/material';
import useLocalization from '../../hooks/useLocalization';
import useDisplayContentConfig from '../../hooks/useDisplayContentConfig';
import { mergeSx } from '../../utils/styles';
import useAuthForms from '../../hooks/useAuthForms';
import useHandleSignUpParams from '../../hooks/useHandleSignUpParams';
import { SKIN_CONFIGS } from '../../config/skinRegistry/configs';
import { useSkinConfigs } from '../../hooks/useSkinConfigs';

const shineAnimation = keyframes`
  0% {
    left: -300%;
  }
  50% {
    left: 300%;
  }
  100% {
    left: -300%;
  }
`;

const styles = (theme: Theme) => ({
  modern: {
    px: { xs: 1.5, lg: 2 },
    background:
      theme.palette.gradient ||
      `linear-gradient(91.32deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    height: { xs: '32px', lg: '52px' },
    minWidth: { lg: '152px' },
    fontSize: { xs: '12px', lg: '14px' },
    lineHeight: { xs: 1, lg: 'normal' },
    borderRadius: 2,
    whiteSpace: 'nowrap',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(60deg, transparent 42%, ${theme.palette.neutral[25]}50 50%, transparent 58%)`,
      animation: `${shineAnimation} 5s infinite linear`,
    },
    '&:hover': {
      boxShadow: `0 10px 20px -5px ${theme.palette.primary.main}30`,
    },
  },
  common: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: '18px',
    color: theme.palette.secondary.main,
    fontSize: '11px',
    lineHeight: { xs: 1, md: 'normal' },
    fontWeight: 700,
    padding: { xs: '0 12px', md: '0 10px' },
    height: { xs: '32px', md: '36px' },
    width: 'auto',
    minWidth: { xs: '50px', md: '64px' },
    backgroundColor: 'transparent',
    '&:hover': {
      borderColor: '#fff',
      color: '#fff',
      backgroundColor: 'transparent',
    },
  },
});

const RegisterButton = ({ sx }: { sx?: SxProps<Theme> }) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const { openRegisterForm } = useAuthForms();
  useHandleSignUpParams();

  const { isModernBranding } = useDisplayContentConfig();
  const { headerAuthButtonLabelKeys } = useSkinConfigs(SKIN_CONFIGS);

  const style = !isModernBranding ? styles(theme).common : styles(theme).modern;
  const label = t(headerAuthButtonLabelKeys.register);

  return (
    <Button variant='contained' onClick={() => openRegisterForm()} sx={mergeSx(style, sx)}>
      {label}
    </Button>
  );
};

export default RegisterButton;
